// Shared types for the chat-based onboarding + project chat (gated behind the
// `new_chat_experience` experimental flag). Imported by BOTH the client chat UI and the
// `project-chat` Netlify function so the request/response shape stays in one place — the same
// cross-boundary import pattern qaConfig.tsx already uses for polish-pass-types.
//
// The message model is adapted from replayio/nut.new (`app/lib/persistence/message.ts`): a message
// is a flat `{ id, role, content }` record whose `content` is plain markdown text (not provider
// content-blocks), so streamed chunks can be merged by id. We run the agent server-side with the
// Anthropic SDK (via lib/llm.ts) rather than nut.new's remote backend, but keep the same client model.

import type { LoginChoice } from './login-mode.ts'
import { DEFAULT_ENABLED_PASSES } from './polish-pass-types.ts'
import type { ProjectEnvironmentKind, ProjectEnvironmentTrigger } from '../../../src/lib/projectEnvironment.ts'

export type ChatRole = 'user' | 'assistant'

export interface ChatMessageData {
  id: string
  role: ChatRole
  content: string
  /** ISO timestamp, used for ordering. */
  createTime?: string
  /** Stable key identifying a system-generated message (e.g. 'github-pr:ver-…'); absent on anything a
   *  human or the agent said. Mirrors project_chat_messages.source, and is what lets the transcript cap
   *  budget QA's status notices separately from the conversation. */
  source?: string | null
  /** The project member who sent this 'user' turn. The transcript is shared across every member of the
   *  project, so this is what lets each member see who said what and chat as themselves. Set server-side
   *  from the sender's authenticated identity; absent on assistant/system messages. Mirrors
   *  project_chat_messages.author_id / author_name. */
  authorId?: string | null
  authorName?: string | null
}

// ---- Onboarding chat --------------------------------------------------------------------------

/** Draft project configuration assembled over the course of the onboarding conversation. Mirrors the
 *  fields the classic StartQaForm collects; the client flattens it into a createProject payload with
 *  the same qaConfig helpers (buildScopeExplorationPrompt / buildLoginGlobalVariables). */
export interface OnboardingConfig {
  initialExplorationBudget?: number
  continuousSchedule?: import('../../../src/schemas/testingSchedules').TestingScheduleInput
  /** 'all' = explore the whole app; 'focus' = concentrate on scopeAreas / instructions. */
  scopeMode: 'all' | 'focus'
  scopeAreas: string[]
  /** Free-text description of what matters most (used in focus mode). */
  instructions: string
  /** The owner's persistent testing instructions, collected in the onboarding chat before the project
   *  exists. Persisted to projects.testing_guidelines at creation and applied to the first exploration,
   *  to journey discovery, and to later version/PR tests. Unlike `instructions` (which narrows WHICH
   *  areas get explored), these are constraints on HOW the app may be tested. */
  testingGuidelines: string
  /** Optional design document describing intended behavior. */
  designDocument: string
  logins: { email: string; password: string }[]
  /** Whether agents may register their own test accounts. */
  autoCreateAccount: boolean
  /** How the user ANSWERED the login question, when they answered it. `autoCreateAccount` alone can't
   *  say: it defaults to true, so an explicit "let QA register its own account" is indistinguishable
   *  from an untouched login step. Undefined = unanswered. Feeds resolveLoginMode. */
  loginChoice?: LoginChoice
  enabledPolishPasses: string[]
  /** Security is selected separately from ownership authorization. These draft fields let onboarding
   *  mint/show the nonce before the project exists, then persist the exact setup at creation. */
  securityProbingEnabled: boolean
  securityNonce: string
  securityOwnershipExempt: boolean
  /** The per-project credit cap, or null for no cap. Off by default (null): the user opts into a
   *  budget from the onboarding chat or Project Settings; there is no low default that pauses a new
   *  project unexpectedly. */
  budget: number | null
  /** Enable for localhost / VPN-only apps reached through the reverse proxy. */
  useReverseProxy: boolean
  /** Initial environment inferred from the app URL, and the run mode chosen during onboarding. */
  environmentKind?: ProjectEnvironmentKind
  environmentTrigger: ProjectEnvironmentTrigger
  environmentSchedule: 'nightly' | 'weekly'
  /** Optional Replay recording id to seed the project from instead of live exploration. */
  recordingId: string
}

// {url} = the app URL; {repo} = the connected owner/repo. Markdown is rendered for chat-message copy.
export interface OnboardingCopy {
  // Landing hero + URL entry.
  title: string
  subtitle: string
  urlPlaceholder: string
  startSetupLabel: string
  /** The same button for a signed-out visitor, who is sent to sign in before setup opens. */
  signedOutStartLabel: string
  // The "Checking {url}…" placeholder and the detection findings (chat messages).
  checking: string
  findingReachable: string
  // Shown when the probe couldn't confirm whether the site is open (e.g. a client-rendered SPA shell
  // the server-side probe can't inspect). Must NOT claim the site is login-free.
  findingReachableUncertain: string
  findingLogin: string
  findingReverseProxy: string
  // Shown when the probe of a public hostname couldn't connect at all (bad TLS certificate, DNS,
  // timeout): a specific "couldn't reach" narration with {reason} interpolated, then the ask —
  // instead of silently diving into the reverse-proxy flow, which trapped publicly hosted targets
  // behind an endless local-connection retry that never unlocked Start QA.
  findingUnreachable: string
  unreachablePrompt: string
  unreachableLocalLabel: string
  unreachableLocalDescription: string
  unreachablePublicLabel: string
  unreachablePublicDescription: string
  // The login question is asked inline in the transcript using the finding messages above (findingLogin
  // for a confirmed wall, findingReachableUncertain for a can't-tell result) as the prompt, with the
  // login choice buttons rendered below — so there are no separate login title/text strings.
  // GitHub offer + connect sub-flow.
  githubOfferMessage: string
  githubOfferPrompt: string
  githubConnectLabel: string
  githubConnectDescription: string
  scheduleLabel: string
  scheduleDescription: string
  runOnceLabel: string
  runOnceDescription: string
  schedulePrompt: string
  nightlyLabel: string
  nightlyDescription: string
  weeklyLabel: string
  weeklyDescription: string
  githubSkipLabel: string
  githubSkipDescription: string
  githubRepoPrompt: string
  githubRepoConnectLabel: string
  githubConnected: string
  githubDoneIntro: string
  // GitHub-first entry: when the user enters a repository URL as the initial URL, we ask for the
  // deployed app URL up front (githubAppUrl*) and then connect the repo they gave — skipping the
  // usual Connect/Skip offer — narrating that with githubStartConnect. {repo} is interpolated.
  githubAppUrlTitle: string
  githubAppUrlSubtitle: string
  githubStartConnect: string
  // Security-pass offer, asked of everyone after the GitHub step. {url} is interpolated into the offer.
  // securityDeferred covers "set up but not yet confirmed" — the pass is configured and stays inert
  // until the ownership file is live, which is a state the user has to be told about explicitly.
  securityOfferMessage: string
  securityOfferPrompt: string
  securityStartMessage: string
  securityVerified: string
  securitySkipped: string
  securityDeferred: string
  // Website (GitHub-skipped) intro.
  websiteIntro: string
  // Start acknowledgement seeded into the transcript.
  startAck: string
  // Short user-voice transcript narrations for button actions.
  narrationConnect: string
  narrationSkipGithub: string
  narrationSchedule: string
  narrationRunOnce: string
  narrationConnectRepo: string
  narrationSelfSignup: string
  narrationSkipLogin: string
  narrationSetupSecurity: string
  narrationSkipSecurity: string
  // User-voice narrations for the unreachable ask's answers.
  narrationUnreachableLocal: string
  narrationUnreachablePublic: string
  // Rotating tip shown below Start QA (one picked at random).
  tips: string[]
}

export const DEFAULT_ONBOARDING_CONFIG: OnboardingConfig = {
  scopeMode: 'all',
  scopeAreas: [],
  instructions: '',
  testingGuidelines: '',
  designDocument: '',
  logins: [],
  autoCreateAccount: true,
  enabledPolishPasses: [...DEFAULT_ENABLED_PASSES],
  securityProbingEnabled: false,
  securityNonce: '',
  securityOwnershipExempt: false,
  budget: null,
  useReverseProxy: false,
  environmentTrigger: 'manual',
  environmentSchedule: 'nightly',
  recordingId: '',
}

export interface OnboardingChatRequest {
  mode: 'onboarding'
  targetUrl: string
  /** Browser-resolved IANA timezone. Older clients may omit it; the server defaults to US Pacific. */
  timezone?: string
  /** Whether targetUrl is a GitHub repository (setup is routed through the GitHub App instead). */
  isGithub?: boolean
  messages: { role: ChatRole; content: string; source?: string | null }[]
  config: OnboardingConfig
  /** See ProjectChatRequest.trimmed. Onboarding transcripts are short enough that this is normally 0. */
  trimmed?: { messages: number; notices: number }
}

export interface OnboardingChatResponse {
  mode: 'onboarding'
  /** The assistant's text reply for this turn. */
  assistant: string
  /** The draft config after any update_settings tool calls this turn. */
  config: OnboardingConfig
  /** True once the assistant has confirmed the user wants to start — the client then creates the project. */
  ready: boolean
}

// ---- Inline forms (agent-requested) -----------------------------------------------------------

/** A login credential pair the agent asks the user to fill in. Rendered as an inline form so the
 *  password is never typed into (or echoed back through) the chat transcript. */
export interface LoginFormEntry { email: string; password: string }

/** A request from the agent to render an inline form in the chat for the user to fill out, instead
 *  of asking them to type sensitive values (logins) or fiddly key/value pairs (global variables). */
export interface ChatFormRequest {
  // 'security-nonce' is not a form the user fills in — it reveals the ownership card, which owns the
  // exact path and value. The agent asks for it rather than reciting the nonce in prose, so the
  // instructions the user follows are identical whether they arrived via the setup step or by asking.
  kind: 'logins' | 'global_variables' | 'security-nonce' | 'deployment-access'
  /** Short heading shown above the form. */
  title?: string
  /** For global_variables: variable names to prefill as rows (values left blank for the user). */
  variableNames?: string[]
}

// ---- Inline choices (clickable options) -------------------------------------------------------

/** Inline UI a choice can reveal directly below the message, with NO model round-trip: the add-logins
 *  form, or the security-pass ownership card. */
export type ChatInlineUiKind = 'logins' | 'security-nonce'

/** What clicking a choice does:
 *  - `message`: sends `message` to the agent as the user's next turn (exactly as if they typed it).
 *  - `ui`: reveals inline UI (e.g. the add-logins form) below the message immediately — no round-trip,
 *    so the user can act without waiting for the model. */
export type ChatChoiceAction =
  | { kind: 'message'; message: string }
  | { kind: 'ui'; ui: ChatInlineUiKind }

/** A single clickable option the agent (or a built-in flow) offers, rendered as a row in the
 *  transcript instead of asking the user to type a reply. */
export interface ChatChoice {
  /** Stable id, unique within its set. */
  id: string
  /** Label shown to the user (the row's title). */
  label: string
  /** Optional one-line explanation shown under the label, so the user understands what the option
   *  does without extra prose. */
  description?: string
  /** What happens on click. */
  action: ChatChoiceAction
}

/** A set of clickable choices shown together, below the message that offered them. */
export interface ChatChoiceSet {
  /** Optional short line shown above the buttons. */
  prompt?: string
  choices: ChatChoice[]
}

/** The built-in choice set offered when a login wall is hit (during the onboarding website probe, or
 *  when an exploration pauses on a sign-in screen). Shared so the onboarding step, the project chat,
 *  and the agent's `offer_choices` fallback all present the same options. "Provide login credentials"
 *  reveals the add-logins form inline; the other two send the agent a decision message. */
export function loginChoiceSet(prompt?: string): ChatChoiceSet {
  return {
    prompt,
    choices: [
      { id: 'provide-logins', label: 'Provide login credentials', action: { kind: 'ui', ui: 'logins' } },
      { id: 'create-account', label: 'Let QA create a test account', action: { kind: 'message', message: 'Please have QA register its own test account to reach signed-in areas.' } },
      { id: 'skip-auth', label: 'Skip signed-in areas', action: { kind: 'message', message: 'Skip authenticated areas — only test what is reachable without logging in.' } },
    ],
  }
}

/** The built-in offer for the security pass, shown to everyone during onboarding and available from the
 *  agent's `setup_security_pass` tool. "Yes" reveals the ownership card inline rather than round-tripping
 *  the model, because the nonce and its path must be exact and the check is a server call with a retry
 *  loop. The awkward part — where the file goes on their stack — the card hands to the user's own coding
 *  agent as a copyable prompt, and to ours if they ask here instead. */
export function securityChoiceSet(prompt?: string): ChatChoiceSet {
  return {
    prompt,
    choices: [
      {
        id: 'setup-security',
        label: 'Yes, test for security issues',
        description: 'Confirm you own the app, then QA probes for injection, access-control and exposure bugs.',
        action: { kind: 'ui', ui: 'security-nonce' },
      },
      {
        id: 'skip-security',
        label: 'Not now',
        description: 'Stick to functional and polish testing. You can turn this on later in settings.',
        action: { kind: 'message', message: 'Skip the security pass for now.' },
      },
    ],
  }
}

// ---- Streaming protocol -----------------------------------------------------------------------

// Which part of the GitHub testing flow to open the inline panel on. The agent picks this from what
// the user asked for: 'main-branch' / 'pull-requests' jump straight to that single question on an
// already-connected repo; 'all' shows the full panel (and is required when linking a new repo, which
// always asks both questions).
export type GithubPanelFocus = 'all' | 'main-branch' | 'pull-requests'

// Which issue tracker the inline Issue-tracker panel should preselect when it opens (manage_issue_tracker
// tool). Mirrors IssueTrackerProvider in netlify/functions/lib/issue-trackers.ts — defined here (not
// imported) so this shared client/server module stays free of the server-only tracker adapter.
export type IssueTrackerProvider = 'github' | 'gitlab' | 'linear' | 'jira'

// Legacy summary destination event shape, retained for previously stored chat events.
export type SummaryDestinationChannel = 'slack' | 'discord' | 'email'

// The chat endpoint streams newline-delimited JSON (NDJSON) — one ChatStreamEvent per line. The
// event model is adapted from replayio/nut.new's `ChatResponse` discriminated union: assistant text
// arrives as incremental `delta` events keyed by message id (accumulated client-side by mergeMessage),
// with side-band events for status, config/settings updates, forms, and completion.
export type ChatStreamEvent =
  // ALWAYS the first event of a turn, emitted before any (possibly slow) guidance/context loads so the
  // client learns the function actually started — a stream that ends with no `meta` at all was killed
  // before the agent ran. `turnId` is the correlation id stamped on this turn's llm_jobs rows and on
  // whatever failure the client reports, so one turn can be reconstructed from both ends.
  | { kind: 'meta'; turnId: string }
  | { kind: 'status'; status: string }
  | { kind: 'delta'; id: string; text: string }
  | { kind: 'config'; config: OnboardingConfig }
  | { kind: 'ready' }
  // Onboarding only: the user clearly asked to start QA in the chat (the agent called start_project),
  // so actually create/start the project now — don't just prime the Start QA button. The client
  // routes this to the same start path the button would (plain website / reverse proxy / GitHub).
  | { kind: 'start_qa' }
  | { kind: 'form'; form: ChatFormRequest }
  | { kind: 'choices'; choices: ChatChoiceSet }
  | { kind: 'settings'; patch: ProjectSettingsPatch; summary: string }
  // Emitted after a chat tool starts real QA work (a new exploration or a journey re-run) so the
  // client re-fetches the activity timeline and the newly queued task shows up promptly.
  | { kind: 'refresh' }
  // Open the inline GitHub connect/disconnect panel in the running-project chat (manage_github tool).
  // `focus` lets the agent target the part of the flow the user actually asked about — 'main-branch'
  // or 'pull-requests' opens straight into just that testing question on an already-connected repo,
  // instead of walking the whole main-branch → PRs wizard; 'all' (the default) shows the full panel.
  | { kind: 'github'; focus?: GithubPanelFocus; repoUrl?: string; resumePrompt?: string }
  // Open the inline Issue-tracker config panel in the running-project chat (manage_issue_tracker tool).
  // `provider` preselects the tracker the user named (GitHub / GitLab / Linear / Jira); omitted lets
  // the panel default to whatever is already configured (or GitHub for a fresh setup).
  | { kind: 'issue_tracker'; provider?: IssueTrackerProvider }
  // Legacy event; current clients ignore it and no longer render a configuration panel.
  | { kind: 'summary_destination'; channel?: SummaryDestinationChannel }
  // Pause or resume the running project (set_project_status tool). The client applies it via
  // updateProject (action 'update-status'), which enforces access control.
  | { kind: 'project_status'; status: 'active' | 'paused' }
  | { kind: 'error'; error: string }
  | { kind: 'done' }

// ---- Project chat (post-creation overview sidebar) --------------------------------------------

/** Settings a running project's chat agent may change. Applied client-side via updateProject
 *  (action `update-settings`), which enforces access control. Kept intentionally small + safe. */
export interface ProjectSettingsPatch {
  target_url?: string
  name?: string
  budget?: number
  max_containers?: number
  parallel_mcp_enabled?: boolean
  max_parallel_containers?: number
  design_document?: string
  enabled_polish_passes?: string[]

  share_location?: string | null
}

export interface ProjectChatRequest {
  mode: 'project'
  projectId: string
  /** Browser-resolved IANA timezone used to interpret unqualified times in this turn. */
  timezone?: string
  /** Compact, client-assembled snapshot of the project + recent QA activity for grounding. */
  context: string
  /** The transcript, ALREADY trimmed by the client to the shared budget (project-chat-transcript.ts).
   *  `source` marks a system-generated message and is used only for that budgeting. */
  messages: { role: ChatRole; content: string; source?: string | null }[]
  /** What the client's own trim left out, so the server can report the true total rather than only the
   *  remainder it happened to see. Absent from a client that predates client-side trimming. */
  trimmed?: { messages: number; notices: number }
}

export interface ProjectChatResponse {
  mode: 'project'
  assistant: string
  /** Non-null when the agent proposes a settings change for the client to apply. */
  settingsPatch: ProjectSettingsPatch | null
  /** Human-readable summary of the proposed change, shown alongside the message. */
  settingsSummary: string | null
}

export type ProjectChatRequestBody = OnboardingChatRequest | ProjectChatRequest
export type ProjectChatResponseBody = OnboardingChatResponse | ProjectChatResponse
